/**
 * React Query hooks for the e-signing API (@metabus/esign-client).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  ListSignatureRequestsParams,
  ListSignatureRequestsResult,
  SignatureRequestStatus,
} from '@metabus/esign-client';
import { esignClient } from '@/lib/esign';

export const esignKeys = {
  all: ['esign', 'signature-requests'] as const,
  lists: () => [...esignKeys.all, 'list'] as const,
  list: (params?: ListSignatureRequestsParams) => [...esignKeys.lists(), params ?? {}] as const,
  details: () => [...esignKeys.all, 'detail'] as const,
  detail: (trackingId: string) => [...esignKeys.details(), trackingId] as const,
};

/** List signing requests, optionally filtered by status. Polls every 30s so
 * a status change (sent -> viewed -> signed -> completed) shows up without a
 * manual refresh. */
export function useSignatureRequests(
  params?: ListSignatureRequestsParams,
): UseQueryResult<ListSignatureRequestsResult, Error> {
  return useQuery({
    queryKey: esignKeys.list(params),
    queryFn: () => esignClient.listSignatureRequests(params),
    refetchInterval: 30_000,
  });
}

/** Live single-request status lookup (refreshes from Documenso server-side
 * on every call — see the contract doc's single-status GET). */
export function useSignatureRequest(
  trackingId: string | undefined,
): UseQueryResult<SignatureRequestStatus, Error> {
  return useQuery({
    queryKey: esignKeys.detail(trackingId ?? ''),
    queryFn: () => esignClient.getSignatureStatus(trackingId as string),
    enabled: !!trackingId,
  });
}
